// src/api/courses.ts
import { get as storeGet, put as storePut, compositeKey } from "../utils/store";

export type Course = {
  id: number;
  title: string;
  description: string | null;
  short_description: string | null;
  cover_image_url: string | null;
  price: number;
  currency: string;
  duration_days: number;
  morning_hour: number;
  morning_minute: number;
  landing_url: string | null;
  sort: number | null;
  design: {
    course_background_color: string | null;
    dashboard_background_image_url: string | null;
  } | null;
};

// M5-STORE3 (18.09.2026): трёхзначно + cached, форма как у snapshot.ts
// (SnapshotResult) — не вторая схема. "empty" покрывает и легитимно пустой
// каталог (200, items:[]), и 404/400/403/401 — в обоих случаях сервер
// ответил определённо, в кэш не пишем и не читаем (content-access,
// инвариант 4: отказ и отсутствие различаются в журнале, не обязаны для
// клиента — здесь оба "empty" снаружи, а ветки в коде разные). "unavailable"/
// "cached" — не удалось спросить вовсе; каталог не гейтится покупкой (это
// публичная витрина, не user_courses), поэтому риск инварианта 9 ниже, чем у
// snapshot.ts, но механизм тот же: запись в кэше могла быть сделана до того,
// как курс сняли с публикации, и переживает это до истечения STALE_AFTER_MS.
export type CoursesResult =
  | { status: "ok"; data: Course[] }
  | { status: "empty" }
  | { status: "unavailable" }
  | { status: "cached"; data: Course[]; cachedAt: number; stale: boolean };

// Один каталог на домен клиента, не per-course — "all" через тот же
// составной ключ (compositeKey), чтобы не заводить вторую форму ключа рядом
// с content_day/progress_days/badges (см. store.ts, комментарий M5-STORE2).
const CATALOG_CACHE_KEY = compositeKey("courses_catalog", "all");

/** Спросить кэш при сетевом отказе. Пусто в кэше → unavailable, как раньше. */
async function cachedOrUnavailable(): Promise<CoursesResult> {
  const cached = await storeGet<Course[]>(CATALOG_CACHE_KEY);
  if (cached.status === "empty") return { status: "unavailable" };
  console.error(`[courses] served from cache, age=${Date.now() - cached.cachedAt}`);
  return {
    status: "cached",
    data: cached.data,
    cachedAt: cached.cachedAt,
    stale: cached.status === "stale",
  };
}

export async function getCourses(): Promise<CoursesResult> {
  let res: Response;
  try {
    res = await fetch("/api/courses");
  } catch (e) {
    console.error("[courses] network error:", e instanceof Error ? e.message : String(e));
    return cachedOrUnavailable();
  }

  // Легитимно пусто (сервер определённо ответил "доступа/каталога нет") —
  // в кэш не кладём и в кэш не заглядываем, как в content.ts.
  if (res.status === 404 || res.status === 400 || res.status === 403 || res.status === 401) {
    return { status: "empty" };
  }

  if (!res.ok) {
    console.error("[courses] unexpected error status:", res.status);
    return cachedOrUnavailable();
  }

  try {
    const json = await res.json();
    const items: Course[] = Array.isArray(json?.items) ? json.items : [];
    if (items.length === 0) return { status: "empty" };
    await storePut(CATALOG_CACHE_KEY, items, 0);
    return { status: "ok", data: items };
  } catch (e) {
    // Форма контракта — как у snapshot.ts: 200 с нечитаемым телом не отказ
    // спросить (спросили и получили ответ), в кэш не идём.
    console.error("[courses] 200 but json parse failed:", e instanceof Error ? e.message : String(e));
    return { status: "empty" };
  }
}
